/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import docApi from "api";
import React from "react";
import { getPagesViewElementHandle } from "selectors/pagedViewState";
import { getPageAndYPosClickedOn } from "selectors/pageNumber";
import {
  getViewPortWidthWithoutPagesViewScrollbar,
  getViewPortHeightWithoutPagesViewScrollbar,
  getPagesViewBoundingRect
} from "selectors/documentViewSelectors";
import { getPagesViewYPos, getPagesViewXPos } from "selectors/pagedViewState";
import { overlayEvents } from "overlays/OverlayEvents";
import { AUTO_SCROLL_MARGIN, MAX_AUTO_SCROLL_SPEED, COPY_TEXT_PERM } from "constants/constants";
import { getPageHandle, getPageOrigHeight } from "selectors/pageSelectors";
import { trackTextCopy } from "analytics/events";
import { triggerTextCopyEvent } from "publicApis/Events/events";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import { getShouldTriggerContextMenuOnTextSelection } from "selectors/contextMenuSelectors";
import { isAllowed } from "utils/DocPermissionsUtils";
import { contextMenuManager } from "contextMenuManager";
import {
  isTouchSelectionPresent,
  getShouldEnableContextMenuOnTouchDevices,
  isThereAnySelection,
  getTextInSelection,
  getTextSelectionPageRange
} from "dc-preview-utils/src/selectors/touchSelectors";
import { contextMenuTriggerType, clickTypes } from "dc-preview-utils/src/Constants/enums";
import { getShouldRenderEditDropin, getShouldRenderCommentsDropin } from "selectors/overlayDropinSelectors";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { copyTextToClipboard, copyRichTextToClipboard } from "utils/CopyTextUtils";
import { getZoomedQuadsObj, shouldDisableMultiPageSelection } from "utils/SelectionUtils";
import { processAccessibilityNodes } from "stores/accessibility";
import { getActiveVerb } from "selectors/quickToolsSelectors";
import { IMAGE_SELECTION_ALLOWED_QT_VERBS } from "constants/enums";
import { isCopyWithFormattingEnabled } from "../selectors/appSelectors";
import { getLatestHitTestResponse } from "../selectors/documentSelectors";
import * as hitTester from "./HitTester";

let intervalID = 0;

const noop = () => {};

const handleStartSelection = args => {
  const { stores } = args;
  const activeVerb = getActiveVerb(stores);
  const useImageSelection = getLatestHitTestResponse(stores).contentType === hitTester.pageContentTypes.IMAGE;
  if (useImageSelection) {
    const isimageSelectionAllowed = IMAGE_SELECTION_ALLOWED_QT_VERBS.indexOf(activeVerb) !== -1;
    if (isimageSelectionAllowed) {
      return imageSelection(args).then(res => {
        stores.documentStore.setIsImageCurrentSelection(true);
        return res;
      });
    }
    return Promise.resolve("");
  }
  return textSelection(args);
};

const handleSelectTextToPoint = args => {
  const { stores } = args;
  const useImageSelection = getLatestHitTestResponse(stores).contentType === hitTester.pageContentTypes.IMAGE;
  if (useImageSelection) {
    return Promise.resolve("");
  }
  return textSelection(args);
};

export const selectionHandler = (eventName, args) => {
  switch (eventName) {
    case "startSelection":
      return handleStartSelection(args);
    case "selectTextToPoint":
      return handleSelectTextToPoint(args);
    case "keyboardSelection":
      return keyboardSelection(args);
    case "selectWord":
      return selectWord(args);
    case "selectionend":
      return endSelection(args);
    case "mouseUp":
      return handleMouseUp();
    case "mouseMove":
      return handleMouseMove(args);
    case "touchMove":
      return handleTouchMove(args);
    case "keyboardSelectionend":
      return handleKeyboardSelectionEnd(args);
    case "copyText":
      return copyText(args);
    case "selectionclear": {
      if (args.stores) {
        args.stores.documentStore.setIsImageCurrentSelection(false);
      }
      return clearSelection(args);
    }
  }
};

const imageSelection = args => {
  const { startPoint, endPoint, priority } = args;
  document.getSelection().removeAllRanges(); // Remove any existing selections
  return docApi.imageSelection(startPoint, endPoint, priority).catch(noop);
};

const textSelection = args => {
  document.getSelection().removeAllRanges(); // Remove any existing selections
  const { startPoint, endPoint, priority, numOfClicks, stores, getCaret } = args;
  if (shouldDisableMultiPageSelection(stores) && startPoint.pageNum !== endPoint.pageNum) {
    return Promise.resolve();
  }

  if (endPoint.x !== startPoint.x) {
    overlayEvents.trigger("selectTextToPoint");
  }

  const snapToWords =
    (numOfClicks === clickTypes.DOUBLE_CLICK) | (numOfClicks === clickTypes.TRIPLE_CLICK) | clickTypes.NO_CLICK;
  return docApi.textSelection(startPoint, endPoint, priority, snapToWords, getCaret).catch(noop);
};

const keyboardSelection = args => {
  const { endPoint, priority, selectionDirection, snapToWords, jumpTo, getCaret } = args;
  return docApi.keyboardSelection(endPoint, selectionDirection, snapToWords, priority, jumpTo, getCaret).catch(noop);
};

const handleKeyboardSelectionEnd = args => {
  const { quads, selectionPoint, stores, getCaret } = args;
  makeSureSelectionIsInView(quads, selectionPoint, stores);
  if (getCaret) contextMenuManager.clearMenuItems(stores);
};

const copyText = args => {
  const { text, richText, stores } = args;
  if (isCopyWithFormattingEnabled(stores)) copyRichTextToClipboard(text, richText);
  else copyTextToClipboard(text, stores);
  trackTextCopy();
  triggerTextCopyEvent(text);
};

const selectWord = args => {
  const { startPoint, priority } = args;
  return docApi.selectWord(startPoint, priority).catch(noop);
};

const handleMouseMove = args => {
  const { x, y, stores } = args;
  autoScrollPages(x, y, stores);
};

const handleTouchMove = args => {
  const { x, y, pageIndex, stores } = args;
  autoScrollPages(x, y, stores, pageIndex);
};

const handleMouseUp = () => {
  stopAutoScroll();
  return Promise.resolve("");
};

function _triggerContextMenu(stores, quads, contextMenuEvent, pageIndex, triggerType) {
  contextMenuManager.triggerContextMenu(
    contextMenuEvent,
    stores,
    {
      quads,
      pageIndex,
      shouldRenderCommentsDropin: getShouldRenderCommentsDropin(stores)
    },
    triggerType
  );
}

const endSelection = args => {
  const { stores, quads, contextMenuEvent, contentType } = args;
  const zoomedQuadsObj = getZoomedQuadsObj(quads, stores);
  overlayEvents.trigger("selectionend", zoomedQuadsObj);
  if (contentType === hitTester.pageContentTypes.IMAGE) {
    if (getShouldTriggerContextMenuOnTextSelection(stores)) {
      _triggerContextMenu(
        stores,
        zoomedQuadsObj,
        contextMenuEvent,
        zoomedQuadsObj.endPage,
        contextMenuTriggerType.IMAGE_SELECTION
      );
    }
  } else {
    preloadTextInSelection(stores).then(text => {
      if (text) {
        if (
          isPersonalCommentingEnabled(stores) ||
          getShouldUseModernViewer(stores) ||
          getShouldRenderEditDropin(stores)
        ) {
          if (getShouldTriggerContextMenuOnTextSelection(stores)) {
            _triggerContextMenu(
              stores,
              zoomedQuadsObj,
              contextMenuEvent,
              zoomedQuadsObj.endPage,
              contextMenuTriggerType.TEXT_SELECTION
            );
          }
        } else if (getShouldEnableContextMenuOnTouchDevices(stores) && isTouchSelectionPresent(stores)) {
          _triggerContextMenu(
            stores,
            zoomedQuadsObj,
            contextMenuEvent,
            zoomedQuadsObj.startPage,
            contextMenuTriggerType.RIGHT_CLICK
          );
        }
      } else if (getShouldTriggerContextMenuOnTextSelection(stores) && !isAllowed(COPY_TEXT_PERM)) {
        _triggerContextMenu(
          stores,
          zoomedQuadsObj,
          contextMenuEvent,
          zoomedQuadsObj.endPage,
          contextMenuTriggerType.TEXT_SELECTION
        );
      } else if (!getShouldUseModernViewer(stores)) {
        /**
         * This check has been put in place to resolve ADCS-19181
         * Triggering context menu while using underline/highlight/strikethrough,
         * takes away focus from the corresponding RHP comment item, consquently
         * collapsing the item automatically.
         */
        _triggerContextMenu(
          stores,
          zoomedQuadsObj,
          contextMenuEvent,
          zoomedQuadsObj.endPage,
          contextMenuTriggerType.TEXT_SELECTION
        );
      }
    });
  }
  return Promise.resolve("");
};

export const fetchSelectedContent = (richText = false) => docApi.copyTextInSelection(richText);

export const preloadTextInSelection = stores => {
  if (isAllowed(COPY_TEXT_PERM)) {
    const fetchRichText = isCopyWithFormattingEnabled(stores);
    return fetchSelectedContent(fetchRichText)
      .then(resp => {
        const { text, aNodes } = resp.data;
        if (aNodes) {
          const processedAnodes = aNodes.map(pageAnodes => processAccessibilityNodes(pageAnodes.children));
          stores.touchSelStore.setRichTextInSelection(processedAnodes);
        } else {
          stores.touchSelStore.setRichTextInSelection([]);
        }
        // removed check for resp.data.text to trigger context menu for rotated text.
        if (text && isThereAnySelection(stores)) {
          stores.touchSelStore.setTextInSelection(text);
        } else {
          stores.touchSelStore.setTextInSelection("");
        }

        return Promise.resolve(getTextInSelection(stores));
      })
      .catch(noop);
  }

  return Promise.resolve("");
};

const stopAutoScroll = () => {
  if (intervalID !== 0) {
    clearInterval(intervalID);
    intervalID = 0;
  }
};

// we need to auto scroll pages only when we are dragging near boundary of viewport.
const shouldAutoScrollPages = (xDown, yDown, stores) => {
  let { left, right, bottom, top } = getPagesViewBoundingRect(stores);
  let delta = {
    xLeft: Math.abs(xDown - left - document.documentElement.scrollLeft),
    yTop: Math.abs(yDown - top - document.documentElement.scrollTop),
    xRight: Math.abs(xDown - right - document.documentElement.scrollLeft),
    yBottom: Math.abs(yDown - bottom - document.documentElement.scrollTop)
  };
  let yScroll = 0;
  let xScroll = 0;
  if (delta.xLeft < AUTO_SCROLL_MARGIN)
    xScroll = Math.floor((delta.xLeft / AUTO_SCROLL_MARGIN - 1) * MAX_AUTO_SCROLL_SPEED);
  if (delta.xRight < AUTO_SCROLL_MARGIN)
    xScroll = Math.floor((1 - delta.xRight / AUTO_SCROLL_MARGIN) * MAX_AUTO_SCROLL_SPEED);
  if (delta.yTop < AUTO_SCROLL_MARGIN)
    yScroll = Math.floor((delta.yTop / AUTO_SCROLL_MARGIN - 1) * MAX_AUTO_SCROLL_SPEED);
  if (delta.yBottom < AUTO_SCROLL_MARGIN)
    yScroll = Math.floor((1 - delta.yBottom / AUTO_SCROLL_MARGIN) * MAX_AUTO_SCROLL_SPEED);
  return { xScroll, yScroll };
};

const shouldStopAutoScroll = (x, y, xScroll, yScroll, stores, pageIndex) => {
  let nextX = getPagesViewXPos(stores) + getViewPortWidthWithoutPagesViewScrollbar(stores) + xScroll;
  let nextY = getPagesViewYPos(stores) + getViewPortHeightWithoutPagesViewScrollbar(stores) + yScroll;
  // stop autoscrolling if we have reached the page limit.
  if (pageIndex !== -1 && pageIndex !== getPageAndYPosClickedOn(stores, y).pageIndex) return true;

  // stop autoscrolling if we have reached scroll limit.
  if (
    stores.pagedViewState.pagesViewXPos + xScroll < 0 ||
    stores.pagedViewState.pagesViewYPos + yScroll < 0 ||
    nextY > getPagesViewElementHandle(stores).scrollHeight ||
    nextX > getPagesViewElementHandle(stores).scrollWidth
  )
    return true;

  return false;
};

const autoScrollPages = (xDown, yDown, stores, pageIndex = -1) => {
  // stop earlier autoscroll if any.
  stopAutoScroll();

  // auto scroll pages if we extending selection beyond page view
  let { xScroll, yScroll } = shouldAutoScrollPages(xDown, yDown, stores);
  // scroll if xScroll or yScroll is non-zero.
  if (xScroll !== 0 || yScroll !== 0) {
    const autoScrollInterval = 16; // time between autoscroll events
    intervalID = setInterval(
      (xScroll, yScroll, pageIndex) => {
        if (shouldStopAutoScroll(xDown, yDown, xScroll, yScroll, stores, pageIndex)) stopAutoScroll();
        else stores.pagedViewState.scrollPagesViewBy(xScroll, yScroll);
      },
      autoScrollInterval,
      xScroll,
      yScroll,
      pageIndex
    );
  }
};

const makeSureSelectionIsInView = (originalQuads, selectionPoint, stores) => {
  const quads = getZoomedQuadsObj(originalQuads, stores);
  if (quads) {
    const startPageAccessor = `page${quads.startPage}`;
    const endPageAccessor = `page${quads.endPage}`;
    const startBBox = "bbox0";
    const endBBox = `bbox${quads[endPageAccessor].bboxCount - 1}`;

    let l = quads[startPageAccessor][startBBox].deviceLeft;
    let t = quads[startPageAccessor][startBBox].deviceTop;
    let r = quads[endPageAccessor][endBBox].deviceRight;
    let b = quads[endPageAccessor][endBBox].deviceBottom;

    let tlDistance = Math.abs(selectionPoint.x - l) + Math.abs(selectionPoint.y - t);
    let brDistance = Math.abs(selectionPoint.x - r) + Math.abs(selectionPoint.y - b);
    if (tlDistance < brDistance) {
      bringPointInView(r, b, quads.endPage, stores, originalQuads);
    } else {
      bringPointInView(l, t, quads.startPage, stores, originalQuads);
    }
  }
};

const bringPointInView = (x, y, pageIndex, stores, originalQuads) => {
  let { left, right, bottom, top } = getPagesViewBoundingRect(stores);
  const pageRect = getPageHandle(stores, pageIndex).getBoundingClientRect();
  if (pageRect.left === 0 && pageRect.right === 0 && pageRect.top === 0 && pageRect.bottom === 0) {
    // Call function to jump to location as, page is no longer on react virtualized grid and so scroll
    // calculations won't work
    stores.pagedViewState.goToLocation(
      pageIndex,
      originalQuads[0].quads[0][0],
      getPageOrigHeight(stores, pageIndex) - Math.max(originalQuads[0].quads[0][1], originalQuads[0].quads[0][5])
    );
    return;
  }
  let xInScreen = x + pageRect.left;
  let yInScreen = y + pageRect.top;

  if (xInScreen < left + document.documentElement.scrollLeft)
    stores.pagedViewState.scrollPagesViewBy(
      xInScreen - left - document.documentElement.scrollLeft - AUTO_SCROLL_MARGIN,
      0
    );
  if (xInScreen > right + document.documentElement.scrollLeft)
    stores.pagedViewState.scrollPagesViewBy(
      xInScreen - right - document.documentElement.scrollLeft + AUTO_SCROLL_MARGIN,
      0
    );
  if (yInScreen < top + document.documentElement.scrollTop)
    stores.pagedViewState.scrollPagesViewBy(
      0,
      yInScreen - top - document.documentElement.scrollTop - AUTO_SCROLL_MARGIN
    );
  if (yInScreen > bottom + document.documentElement.scrollTop)
    stores.pagedViewState.scrollPagesViewBy(
      0,
      yInScreen - bottom - document.documentElement.scrollTop + AUTO_SCROLL_MARGIN
    );
};

const clearSelection = args => {
  const { stores, quads } = args;
  const zoomedQuadsObj = getZoomedQuadsObj(quads, stores);

  // trigger clear selection event.. eureka needs this event to hide thier contextual tools.
  overlayEvents.trigger("selectionclear", zoomedQuadsObj);
  contextMenuManager.clearMenuItems(stores);
};
